import {
	Box,
	Card,
	CardSection,
	createStyles,
	Flex,
	Group,
	Stack,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import type { Filter } from '@repo/api-codegen/api';
import { IconButton, Text } from '@repo/foundations';
import { useState } from 'react';
import { useCatalogList } from '../../api/hooks/resourceCatalog';
import { ResourceDisplay } from '../../pages/PoliciesPage/render';
import { getCountDisplay } from '../../pages/PoliciesPage/utils';
import SearchBox from '../SearchBox/SearchBox';

const useStyles = createStyles((theme) => ({
	row: {
		borderBottom: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		padding: theme.spacing.xs,
		paddingLeft: theme.spacing.sm,
	},
	stackContainer: {
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		fontSize: theme.fontSizes.xs,
		color: theme.other.getColor('text/secondary/default'),
	},
}));

export default function PreviewModalContent({ filter }: { filter: Filter }) {
	const { classes, theme } = useStyles();
	const [search, setSearch] = useState('');
	const [debouncedSearch] = useDebouncedValue(search, 100);
	const [page, setPage] = useState(1);

	const { data: dataWithSearch, isLoading } = useCatalogList({
		page,
		filters: {
			filter,
			search_term: debouncedSearch,
			grab_all_entity_types: true,
		},
	});

	return (
		<Stack p={0}>
			<SearchBox
				variant="tertiary"
				placeholder="Search resources"
				onSearch={setSearch}
			/>
			<Card radius="md" className={classes.stackContainer}>
				<CardSection
					sx={{
						backgroundColor: theme.other.getColor('fill/secondary/default'),
					}}
					p="xs"
				>
					<Text size="sm" color="text/secondary/default">
						Resources
					</Text>
				</CardSection>
				<CardSection p="0">
					<Flex h="50vh" direction="column" sx={{ overflowY: 'auto' }}>
						{dataWithSearch?.results.map((result) => (
							<Box key={result.id} className={classes.row}>
								<ResourceDisplay record={result} />
							</Box>
						))}
					</Flex>
				</CardSection>
				<CardSection
					sx={{
						backgroundColor: theme.other.getColor('fill/secondary/default'),
					}}
				>
					<Group position="apart" p="xs" noWrap>
						<Text color="text/secondary/default" size="xs">
							{isLoading
								? 'Loading...'
								: `${(page - 1) * 50 + 1}-${Math.min(page * 50, dataWithSearch?.count ?? 0)} of ${getCountDisplay(dataWithSearch?.count)} results`}
						</Text>
						<Group spacing="xs">
							<IconButton
								disabled={!dataWithSearch?.meta.previous_page}
								variant="tertiary"
								iconName="chevronLeft"
								onClick={() => setPage(page - 1)}
							/>
							<IconButton
								disabled={!dataWithSearch?.meta.next_page}
								variant="tertiary"
								iconName="chevronRight"
								onClick={() => setPage(page + 1)}
							/>
						</Group>
					</Group>
				</CardSection>
			</Card>
		</Stack>
	);
}
